/**
 * Elite Batch 11 Part 4 — Workflow Templates 1008–1027
 * Hashtag tracking, high-LTV winback, holiday calendars, homepage opt-in,
 * hook testing (Kling/TikTok, UGC), hot leads audience push, how-to tutorials,
 * HubSpot auto-create, incentive-ladder winback, influencer outreach,
 * inspirational quotes, Instagram cross-post/calendar/post, international welcome,
 * invalid phone cleanup, Klaviyo birthday/browse-abandonment/product-review.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  mediaUrl: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "text", value: text },
          { key: "media[link]", value: mediaUrl },
          { key: "shorten", value: "true" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: outputs,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 11 PART 4 — TEMPLATES 1008–1027
// =============================================================================

export const ELITE_BATCH11_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1008. HASHTAG PERFORMANCE TRACKER (7 nodes)
  // ===================================================================

  "Hashtag Performance Tracker": {
    summary:
      "Tracks hashtag performance across multiple social platforms on a daily schedule. Collects engagement metrics from Instagram and TikTok, aggregates the data via runCode, uses AI to analyze performance trends and identify top performers, checks whether any hashtag exceeds the alert threshold, pushes results to a Google Sheets dashboard, and sends a weekly digest email with recommendations.",
    nodes: [
      trigger("Schedule: Daily hashtag performance scan", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to collect and analyze hashtag performance metrics across Instagram and TikTok",
        setup:
          "Configure Instagram Graph API and TikTok Research API tokens. Set up Google Sheets for the performance dashboard. Define hashtag alert thresholds.",
        inputFields: [],
      }),

      // 2. Multi-platform data collection
      n("2", "hitApi", "Collect multi-platform hashtag metrics", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v19.0/ig_hashtag_search?q={tracked_hashtags}&user_id={ig_user_id}&access_token={ig_access_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "data", customName: "hashtag_raw_data", type: "string" },
            {
              name: "data[0].id",
              customName: "primary_hashtag_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. Performance aggregation (runCode)
      n("3", "runCode", "Aggregate hashtag performance metrics", 520, {
        runCodeDetails: {
          code: `// Parse multi-platform hashtag data and compute aggregated metrics
const rawData = JSON.parse(variables.hashtag_raw_data || '[]');
const metrics = rawData.map(h => ({
  hashtag: h.name || h.hashtag_name,
  posts: h.media_count || h.video_count || 0,
  reach: h.reach || h.view_count || 0,
  engagement: h.engagement || h.like_count || 0,
  growth_pct: h.media_count > 0 ? ((h.recent_count || 0) / h.media_count * 100).toFixed(2) : '0.00'
}));
const totalReach = metrics.reduce((s, m) => s + m.reach, 0);
const topPerformer = metrics.sort((a, b) => b.engagement - a.engagement)[0] || { hashtag: 'none' };
const alertTriggered = metrics.some(m => parseFloat(m.growth_pct) > 15);
return { aggregated_metrics: JSON.stringify(metrics), total_reach: totalReach, top_hashtag: topPerformer.hashtag, alert_triggered: alertTriggered, hashtag_count: metrics.length };`,
          outputs: [
            { name: "aggregated_metrics", type: "string" },
            { name: "total_reach", type: "number" },
            { name: "top_hashtag", type: "string" },
            { name: "alert_triggered", type: "boolean" },
            { name: "hashtag_count", type: "number" },
          ],
        },
      }),

      // 4. AI performance analysis
      aiNode(
        "4",
        "AI: Analyze hashtag performance trends",
        760,
        "You are a social media analytics expert. Analyze the aggregated hashtag performance metrics and identify trends, top performers, underperformers, and actionable recommendations. Respond JSON: {analysis_summary: string, top_hashtags: string, underperformers: string, trend_direction: string, recommendations: string, alert_message: string}",
        "Metrics: {aggregated_metrics}. Total reach: {total_reach}. Top hashtag: {top_hashtag}. Count: {hashtag_count}.",
        [
          {
            field: "analysis_summary",
            as: "performance_summary",
            type: "string",
          },
          { field: "top_hashtags", as: "top_hashtags_list", type: "string" },
          {
            field: "underperformers",
            as: "underperformers_list",
            type: "string",
          },
          { field: "trend_direction", as: "trend_direction", type: "string" },
          {
            field: "recommendations",
            as: "hashtag_recommendations",
            type: "string",
          },
          { field: "alert_message", as: "alert_message", type: "string" },
        ],
      ),

      // 5. Threshold alert condition
      n("5", "condition", "Any hashtag exceeds alert threshold?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{alert_triggered}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 6. Performance dashboard (Sheets)
      sheetsNode(
        "6",
        "Update performance dashboard (Sheets)",
        1240,
        "Hashtag_Performance",
        '[["{top_hashtag}","{total_reach}","{hashtag_count}","{trend_direction}","{performance_summary}","{top_hashtags_list}","{underperformers_list}","=NOW()"]]',
      ),

      // 7. Weekly digest email
      emailNode(
        "7",
        "Send weekly hashtag digest",
        1480,
        "{marketing_team_email}",
        "Hashtag Performance Report — {top_hashtag} leading",
        "Hi Team,\\n\\nDaily Hashtag Performance Summary:\\n\\n{performance_summary}\\n\\nTop Performers: {top_hashtags_list}\\nUnderperformers: {underperformers_list}\\nTrend: {trend_direction}\\n\\nRecommendations:\\n{hashtag_recommendations}\\n\\nAlert: {alert_message}\\n\\nFull dashboard: [Google Sheets link]",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1009. HIGH-LTV WINBACK (8 nodes)
  // ===================================================================

  "High-LTV Winback": {
    summary:
      "Re-engages high-lifetime-value customers who have gone dormant. Scans for dormant high-LTV contacts on schedule, pulls full customer history from HubSpot, uses AI to craft a personalized premium winback email, dispatches it, waits and checks for engagement, follows up via SMS for non-responders, sends a premium second email, and logs the outcome to Sheets.",
    nodes: [
      trigger("Schedule: High-LTV dormant customer scan", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to identify high-LTV customers who have been dormant for 60+ days and initiate a premium winback sequence",
        setup:
          "Configure HubSpot with LTV fields and last-activity tracking. Set up Twilio for SMS outreach. Prepare Google Sheets for outcome tracking.",
        inputFields: [],
      }),

      // 2. Customer history deep-dive (HubSpot)
      hubspotPostNode(
        "2",
        "Fetch high-LTV dormant contacts (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_calculated_merged_vids","operator":"HAS_PROPERTY"},{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"},{"propertyName":"notes_last_updated","operator":"LT","value":"{sixty_days_ago_ms}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","hs_object_id","total_revenue","lifecyclestage","notes_last_updated","phone","company"]',
          },
          { key: "limit", value: "50" },
        ],
        [
          { name: "results", customName: "dormant_customers", type: "string" },
          { name: "total", customName: "dormant_count", type: "number" },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "customer_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.total_revenue",
            customName: "customer_ltv",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "customer_phone",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "customer_company",
            type: "string",
          },
        ],
      ),

      // 3. AI premium winback email #1
      aiNode(
        "3",
        "AI: Craft premium winback email #1",
        520,
        "You are a premium customer retention specialist. Craft a warm, personalized winback email for a high-LTV customer who has gone dormant. Reference their past value, acknowledge the lapse without guilt-tripping, and offer a compelling reason to re-engage. Respond JSON: {subject: string, body: string, cta_text: string, incentive_offer: string, tone_notes: string}",
        "Customer: {customer_first_name} at {customer_company}. LTV: ${customer_ltv}. Dormant since: {notes_last_updated}.",
        [
          { field: "subject", as: "winback_subject_1", type: "string" },
          { field: "body", as: "winback_body_1", type: "string" },
          { field: "cta_text", as: "winback_cta_1", type: "string" },
          { field: "incentive_offer", as: "incentive_1", type: "string" },
          { field: "tone_notes", as: "tone_notes", type: "string" },
        ],
      ),

      // 4. Email dispatch
      emailNode(
        "4",
        "Send premium winback email #1",
        760,
        "{customer_email}",
        "{winback_subject_1}",
        "{winback_body_1}",
      ),

      // 5. Engagement monitor (delay + condition)
      n("5", "delayAction", "Wait 5 days for engagement", 1000, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 5, unit: "days" },
        },
      }),

      // 5b. Condition
      n("5b", "condition", "Customer re-engaged?", 1180, {
        conditionDetails: {
          conditions: [
            {
              variable: "{customer_email_opened}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 6. Personal phone/SMS outreach (Twilio) — False branch
      n(
        "6",
        "sendSMS",
        "SMS: Personal outreach to dormant VIP",
        1420,
        {
          sendSmsDetails: {
            to: "{customer_phone}",
            content:
              "Hi {customer_first_name}, this is your account team. We noticed you haven't been around lately and wanted to personally check in. We have {incentive_1} waiting for you. Reply YES to reconnect or let us know how we can help.",
          },
        },
        350,
      ),

      // 7. AI premium email #2
      aiNode(
        "7",
        "AI: Craft premium winback email #2",
        1660,
        "You are a premium customer retention specialist. This is the second winback attempt for a high-LTV customer who did not respond to the first email or SMS. Escalate the offer and urgency slightly. Reference the SMS touchpoint. Respond JSON: {subject: string, body: string, escalated_offer: string, urgency_note: string}",
        "Customer: {customer_first_name} at {customer_company}. LTV: ${customer_ltv}. Previous offer: {incentive_1}. No response to email #1 or SMS.",
        [
          { field: "subject", as: "winback_subject_2", type: "string" },
          { field: "body", as: "winback_body_2", type: "string" },
          { field: "escalated_offer", as: "escalated_offer", type: "string" },
          { field: "urgency_note", as: "urgency_note", type: "string" },
        ],
      ),

      // 8. Outcome tracker (Sheets)
      sheetsNode(
        "8",
        "Log winback sequence outcome (Sheets)",
        1900,
        "HighLTV_Winback",
        '[["{customer_email}","{customer_first_name}","{customer_company}","{customer_ltv}","{winback_subject_1}","{incentive_1}","{escalated_offer}","{dormant_count}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "5b"),
      e("5b", "8", "output_1"),
      e("5b", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1010. HOLIDAY CAMPAIGN CALENDAR SUBSCRIBERS (6 nodes)
  // ===================================================================

  "Holiday Campaign Calendar Subscribers": {
    summary:
      "Compiles and distributes a promotional holiday calendar to segmented subscriber lists. A scheduled trigger kicks off calendar compilation, runCode builds the holiday promotional calendar with dates and offers, AI crafts an engaging calendar email, subscribers are segmented by engagement tier, the calendar email is dispatched, and engagement is tracked with follow-up reminders logged to Sheets.",
    nodes: [
      trigger("Schedule: Holiday calendar compilation", "schedule", {
        schedule: "0 10 1 11 *",
        howItStarts:
          "Fires on November 1st to compile the upcoming holiday promotional calendar and distribute to subscribers",
        setup:
          "Prepare holiday promotion schedule with dates and offers. Configure subscriber segments in your email platform. Set up Google Sheets for engagement tracking.",
        inputFields: [],
      }),

      // 2. Promotional calendar builder (runCode)
      n("2", "runCode", "Build holiday promotional calendar", 280, {
        runCodeDetails: {
          code: `// Compile the holiday promotional calendar with key dates, offers, and themes
const holidays = [
  { date: '2026-11-27', name: 'Black Friday', offer: '40% off sitewide', theme: 'doorbuster' },
  { date: '2026-11-30', name: 'Cyber Monday', offer: 'BOGO free on select items', theme: 'digital' },
  { date: '2026-12-02', name: 'Giving Tuesday', offer: '10% donated to charity per order', theme: 'charity' },
  { date: '2026-12-15', name: 'Free Shipping Day', offer: 'Free express shipping', theme: 'urgency' },
  { date: '2026-12-20', name: 'Last Chance Sale', offer: '25% off + guaranteed delivery', theme: 'urgency' },
  { date: '2026-12-26', name: 'Boxing Day', offer: 'Up to 60% clearance', theme: 'clearance' }
];
const calendarHtml = holidays.map(h => '<tr><td>' + h.date + '</td><td>' + h.name + '</td><td>' + h.offer + '</td></tr>').join('');
return { calendar_data: JSON.stringify(holidays), calendar_html: calendarHtml, total_promotions: holidays.length, first_date: holidays[0].date, last_date: holidays[holidays.length - 1].date };`,
          outputs: [
            { name: "calendar_data", type: "string" },
            { name: "calendar_html", type: "string" },
            { name: "total_promotions", type: "number" },
            { name: "first_date", type: "string" },
            { name: "last_date", type: "string" },
          ],
        },
      }),

      // 3. AI calendar email
      aiNode(
        "3",
        "AI: Craft engaging holiday calendar email",
        520,
        "You are a holiday marketing email specialist. Create an engaging email that presents the holiday promotional calendar to subscribers. Build excitement, highlight the best deals, and encourage subscribers to mark their calendars. Respond JSON: {subject: string, preview_text: string, body_html: string, top_deal_highlight: string, urgency_cta: string}",
        "Calendar data: {calendar_data}. Total promotions: {total_promotions}. Date range: {first_date} to {last_date}.",
        [
          { field: "subject", as: "calendar_subject", type: "string" },
          { field: "preview_text", as: "calendar_preview", type: "string" },
          { field: "body_html", as: "calendar_body", type: "string" },
          { field: "top_deal_highlight", as: "top_deal", type: "string" },
          { field: "urgency_cta", as: "calendar_cta", type: "string" },
        ],
      ),

      // 4. Subscriber segmentation
      n("4", "runCode", "Segment subscribers by engagement tier", 760, {
        runCodeDetails: {
          code: `// Segment the subscriber list by engagement tiers for personalized send timing
const segments = {
  vip: { tier: 'VIP', send_priority: 1, extra_offer: 'Early access 24h before each sale' },
  engaged: { tier: 'Engaged', send_priority: 2, extra_offer: 'Free gift wrapping on orders over $50' },
  dormant: { tier: 'Win-back', send_priority: 3, extra_offer: 'Extra 5% off first holiday purchase' }
};
return { subscriber_segments: JSON.stringify(segments), segment_count: Object.keys(segments).length, vip_extra: segments.vip.extra_offer, engaged_extra: segments.engaged.extra_offer };`,
          outputs: [
            { name: "subscriber_segments", type: "string" },
            { name: "segment_count", type: "number" },
            { name: "vip_extra", type: "string" },
            { name: "engaged_extra", type: "string" },
          ],
        },
      }),

      // 5. Calendar email dispatch
      emailNode(
        "5",
        "Dispatch holiday calendar email",
        1000,
        "{subscriber_email}",
        "{calendar_subject}",
        "{calendar_body}\\n\\nSpecial for you: {vip_extra}\\n\\n{calendar_cta}",
      ),

      // 6. Engagement tracker + reminder (delay + Sheets)
      sheetsNode(
        "6",
        "Track engagement & schedule reminders (Sheets)",
        1240,
        "Holiday_Calendar",
        '[["{subscriber_email}","{calendar_subject}","{total_promotions}","{top_deal}","{segment_count}","{first_date}","{last_date}","sent","=NOW()"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1011. HOMEPAGE HERO OPT-IN (6 nodes)
  // ===================================================================

  "Homepage Hero Opt-In": {
    summary:
      "Captures email opt-ins from homepage hero section engagement. A webhook fires when a visitor interacts with the hero CTA, runCode checks visitor eligibility and deduplication, AI selects the most relevant value proposition based on referral source, a dynamic opt-in message is rendered, captured emails are stored in HubSpot with consent, and an immediate welcome sequence is triggered.",
    nodes: [
      trigger("Webhook: Hero section CTA engagement", "webhook_app", {
        source: "website",
        event: "hero_cta_click",
        howItStarts:
          "Fires when a visitor clicks the hero section CTA on the homepage, capturing referral source, page variant, and session data",
        setup:
          "Add the webhook trigger to your homepage hero CTA. Configure HubSpot for contact creation with consent tracking. Set up welcome email automation.",
        inputFields: [],
      }),

      // 2. Visitor eligibility check (runCode)
      n("2", "runCode", "Check visitor eligibility & deduplicate", 280, {
        runCodeDetails: {
          code: `// Parse webhook payload and check visitor eligibility
const payload = JSON.parse(variables.webhook_payload || '{}');
const visitorId = payload.visitor_id || payload.session_id || 'unknown';
const referralSource = payload.referrer || payload.utm_source || 'direct';
const pageVariant = payload.variant || payload.page_version || 'A';
const isReturning = payload.visit_count > 1;
const isEligible = !payload.already_subscribed && visitorId !== 'unknown';
return { visitor_id: visitorId, referral_source: referralSource, page_variant: pageVariant, is_returning: isReturning, is_eligible: isEligible, device_type: payload.device || 'desktop' };`,
          outputs: [
            { name: "visitor_id", type: "string" },
            { name: "referral_source", type: "string" },
            { name: "page_variant", type: "string" },
            { name: "is_returning", type: "boolean" },
            { name: "is_eligible", type: "boolean" },
            { name: "device_type", type: "string" },
          ],
        },
      }),

      // 3. Dynamic value prop selector
      aiNode(
        "3",
        "AI: Select best value proposition",
        520,
        "You are a conversion optimization specialist. Based on the visitor's referral source, device type, and whether they are returning, select the most compelling value proposition and CTA copy for the opt-in form. Respond JSON: {value_prop: string, headline: string, subheadline: string, cta_button_text: string, incentive: string, social_proof: string}",
        "Referral: {referral_source}. Device: {device_type}. Returning visitor: {is_returning}. Page variant: {page_variant}.",
        [
          { field: "value_prop", as: "value_prop", type: "string" },
          { field: "headline", as: "optin_headline", type: "string" },
          { field: "subheadline", as: "optin_subheadline", type: "string" },
          { field: "cta_button_text", as: "cta_button_text", type: "string" },
          { field: "incentive", as: "optin_incentive", type: "string" },
          { field: "social_proof", as: "social_proof_line", type: "string" },
        ],
      ),

      // 4. Opt-in form renderer (sendMessage)
      n("4", "sendMessage", "Render dynamic opt-in form", 760, {
        sendMessageDetails: {
          content:
            "**{optin_headline}**\\n\\n{optin_subheadline}\\n\\n{social_proof_line}\\n\\nGet: {optin_incentive}\\n\\n[{cta_button_text}]",
        },
      }),

      // 5. Email capture + consent (HubSpot)
      hubspotPostNode(
        "5",
        "Create contact with consent (HubSpot)",
        1000,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{captured_email}","firstname":"{captured_name}","hs_lead_status":"NEW","lead_source":"homepage_hero","utm_source":"{referral_source}","device_type":"{device_type}","optin_variant":"{page_variant}","consent_given":"true","consent_timestamp":"{current_timestamp}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_email",
            type: "string",
          },
          {
            name: "createdAt",
            customName: "contact_created_at",
            type: "string",
          },
        ],
      ),

      // 6. Immediate welcome trigger
      emailNode(
        "6",
        "Send immediate welcome email",
        1240,
        "{confirmed_email}",
        "Welcome! Here's your {optin_incentive}",
        "Hi {captured_name},\\n\\nThank you for joining us! As promised, here is your {optin_incentive}.\\n\\n{value_prop}\\n\\nWe're excited to have you. Stay tuned for exclusive content and offers.\\n\\nBest regards,\\nThe Team",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1012. HOOK TEST MATRIX KLING + TIKTOK (8 nodes)
  // ===================================================================

  "Hook Test Matrix Kling + TikTok": {
    summary:
      "Runs a structured hook-testing matrix using Kling AI for video generation and TikTok for distribution. A webhook captures the test brief, AI generates multiple hook scripts with different angles, videos are batch-rendered via Kling, runCode validates video quality, approved videos are uploaded to TikTok in batch, a delay monitors early performance, AI analyzes the winner, and results are reported to Sheets and Slack.",
    nodes: [
      trigger("Webhook: Hook test brief received", "webhook_app", {
        source: "internal",
        event: "hook_test_brief",
        howItStarts:
          "Fires when a content strategist submits a hook test brief specifying the product, audience, and hook angles to test",
        setup:
          "Configure Kling AI API for video rendering. Set up TikTok Business API for batch uploads. Connect Slack for results reporting and Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. AI hook script generation
      aiNode(
        "2",
        "AI: Generate hook scripts for testing",
        280,
        "You are a viral content strategist specializing in short-form video hooks. Given a test brief, generate 4-6 distinct hook scripts, each testing a different psychological angle (curiosity, shock, relatability, authority, FOMO, humor). Each hook should be 3-5 seconds of opening content. Respond JSON: {hooks: string, hook_count: number, angles_tested: string, product_name: string, target_audience: string, hypothesis: string}",
        "Test brief: {webhook_payload}",
        [
          { field: "hooks", as: "hook_scripts", type: "string" },
          { field: "hook_count", as: "hook_count", type: "number" },
          { field: "angles_tested", as: "angles_tested", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "hypothesis", as: "test_hypothesis", type: "string" },
        ],
      ),

      // 3. Batch video rendering (Kling)
      n("3", "hitApi", "Batch render hook videos (Kling AI)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.klingai.com/v1/videos/text2video",
          headers: [
            { key: "Authorization", value: "Bearer {kling_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "prompt", value: "{hook_scripts}" },
            { key: "model_name", value: "kling-v1" },
            { key: "duration", value: "5" },
            { key: "aspect_ratio", value: "9:16" },
            { key: "cfg_scale", value: "0.5" },
          ],
          variables: [
            {
              name: "data.task_id",
              customName: "render_task_id",
              type: "string",
            },
            {
              name: "data.task_status",
              customName: "render_status",
              type: "string",
            },
            {
              name: "data.videos",
              customName: "rendered_videos",
              type: "string",
            },
          ],
        },
      }),

      // 4. Video quality validation (runCode)
      n("4", "runCode", "Validate video quality and specs", 760, {
        runCodeDetails: {
          code: `// Validate rendered videos meet TikTok specs and quality thresholds
const videos = JSON.parse(variables.rendered_videos || '[]');
const validated = videos.map((v, i) => ({
  index: i,
  url: v.url || v.video_url,
  duration: v.duration || 5,
  resolution: v.resolution || '1080x1920',
  meets_spec: (v.duration || 5) >= 3 && (v.duration || 5) <= 60,
  quality_score: v.quality_score || 0.85
}));
const allPass = validated.every(v => v.meets_spec && v.quality_score >= 0.7);
const passCount = validated.filter(v => v.meets_spec).length;
return { validated_videos: JSON.stringify(validated), all_pass: allPass, pass_count: passCount, total_videos: validated.length, video_urls: validated.map(v => v.url).join(',') };`,
          outputs: [
            { name: "validated_videos", type: "string" },
            { name: "all_pass", type: "boolean" },
            { name: "pass_count", type: "number" },
            { name: "total_videos", type: "number" },
            { name: "video_urls", type: "string" },
          ],
        },
      }),

      // 5. TikTok batch upload
      n("5", "hitApi", "Upload hook videos to TikTok", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://open.tiktokapis.com/v2/post/publish/video/init/",
          headers: [
            { key: "Authorization", value: "Bearer {tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "post_info",
              value:
                '{"title":"Hook Test: {product_name} — {angles_tested}","privacy_level":"PUBLIC_TO_EVERYONE","disable_comment":false}',
            },
            {
              key: "source_info",
              value: '{"source":"PULL_FROM_URL","video_url":"{video_urls}"}',
            },
          ],
          variables: [
            {
              name: "data.publish_id",
              customName: "tiktok_publish_id",
              type: "string",
            },
            {
              name: "data.upload_url",
              customName: "tiktok_upload_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Performance monitor (delay)
      n("6", "delayAction", "Wait 48 hours for performance data", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 48, unit: "hours" },
        },
      }),

      // 7. AI winner analysis
      aiNode(
        "7",
        "AI: Analyze hook test winner",
        1480,
        "You are a performance marketing analyst. Analyze the TikTok hook test results and determine the winning hook based on view-through rate, engagement, and shares. Provide actionable insights for scaling. Respond JSON: {winning_hook: string, winning_angle: string, best_vtr: string, engagement_summary: string, scale_recommendation: string, next_test_suggestion: string}",
        "Product: {product_name}. Hooks tested: {hook_count}. Angles: {angles_tested}. Hypothesis: {test_hypothesis}. Videos uploaded: {pass_count}. Publish ID: {tiktok_publish_id}.",
        [
          { field: "winning_hook", as: "winning_hook", type: "string" },
          { field: "winning_angle", as: "winning_angle", type: "string" },
          { field: "best_vtr", as: "best_view_through_rate", type: "string" },
          {
            field: "engagement_summary",
            as: "engagement_summary",
            type: "string",
          },
          {
            field: "scale_recommendation",
            as: "scale_recommendation",
            type: "string",
          },
          { field: "next_test_suggestion", as: "next_test", type: "string" },
        ],
      ),

      // 8. Results report (Sheets + Slack)
      sheetsNode(
        "8",
        "Log hook test results (Sheets)",
        1720,
        "Hook_Test_Matrix",
        '[["{product_name}","{hook_count}","{angles_tested}","{winning_hook}","{winning_angle}","{best_view_through_rate}","{engagement_summary}","{scale_recommendation}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1013. HOOK TEST UGC ADS (8 nodes)
  // ===================================================================

  "Hook Test UGC Ads": {
    summary:
      "Tests multiple UGC-style ad hooks for authenticity and performance. A webhook captures the UGC brief, AI generates authentic UGC scripts with multiple hooks, videos are rendered in UGC style, a condition reviews content authenticity, an approval gate lets the team greenlight creative, approved hooks are deployed as TikTok test campaigns, performance is tracked in Sheets, and the winner is selected for scaling.",
    nodes: [
      trigger("Webhook: UGC ad test brief received", "webhook_app", {
        source: "internal",
        event: "ugc_test_brief",
        howItStarts:
          "Fires when a paid media team submits a UGC ad test brief with product details, target persona, and hook angles to test",
        setup:
          "Configure video rendering API. Set up TikTok Ads API for campaign creation. Connect Google Sheets for performance tracking. Add approval gate participants.",
        inputFields: [],
      }),

      // 2. AI UGC script generation
      aiNode(
        "2",
        "AI: Generate UGC ad scripts",
        280,
        "You are a UGC content strategist. Generate 3-5 authentic user-generated content scripts with different hook styles. Each should feel natural and unscripted — like a real person sharing their experience. Include on-screen text overlays and background setting descriptions. Respond JSON: {scripts: string, script_count: number, personas: string, product_name: string, hook_styles: string, authenticity_notes: string}",
        "UGC brief: {webhook_payload}",
        [
          { field: "scripts", as: "ugc_scripts", type: "string" },
          { field: "script_count", as: "ugc_script_count", type: "number" },
          { field: "personas", as: "ugc_personas", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "hook_styles", as: "hook_styles", type: "string" },
          {
            field: "authenticity_notes",
            as: "authenticity_notes",
            type: "string",
          },
        ],
      ),

      // 3. Video rendering (multiple hooks)
      n("3", "hitApi", "Render UGC-style hook videos", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_ugc_template}" },
            {
              key: "modifications",
              value:
                '{"scripts":"{ugc_scripts}","personas":"{ugc_personas}","style":"ugc_authentic"}',
            },
          ],
          variables: [
            { name: "id", customName: "ugc_render_id", type: "string" },
            { name: "status", customName: "ugc_render_status", type: "string" },
            { name: "url", customName: "ugc_video_urls", type: "string" },
          ],
        },
      }),

      // 4. Content authenticity review condition
      n("4", "condition", "Videos pass authenticity review?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{ugc_render_status}",
              operator: "equals",
              value: "succeeded",
            },
          ],
        },
      }),

      // 5. Approval gate
      n("5", "approvalGate", "Team creative approval gate", 1000, {
        approvalGateDetails: {
          approvers: ["{creative_lead_email}", "{paid_media_lead_email}"],
          message:
            "UGC hook test videos ready for review.\\n\\nProduct: {product_name}\\nHooks: {hook_styles}\\nPersonas: {ugc_personas}\\nScripts: {ugc_script_count}\\n\\nPlease review for brand safety, authenticity, and ad policy compliance.",
          requiredApprovals: 1,
        },
      }),

      // 6. TikTok test campaign creation
      n("6", "hitApi", "Create TikTok test ad campaign", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://business-api.tiktok.com/open_api/v1.3/campaign/create/",
          headers: [
            { key: "Access-Token", value: "{tiktok_ads_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "advertiser_id", value: "{tiktok_advertiser_id}" },
            {
              key: "campaign_name",
              value: "UGC Hook Test — {product_name} — {hook_styles}",
            },
            { key: "objective_type", value: "VIDEO_VIEWS" },
            { key: "budget_mode", value: "BUDGET_MODE_DAY" },
            { key: "budget", value: "50" },
          ],
          variables: [
            {
              name: "data.campaign_id",
              customName: "tiktok_campaign_id",
              type: "string",
            },
            {
              name: "code",
              customName: "campaign_create_status",
              type: "number",
            },
          ],
        },
      }),

      // 7. Performance tracker (Sheets)
      sheetsNode(
        "7",
        "Track UGC test performance (Sheets)",
        1480,
        "UGC_Hook_Tests",
        '[["{product_name}","{ugc_script_count}","{hook_styles}","{ugc_personas}","{tiktok_campaign_id}","{authenticity_notes}","active","=NOW()"]]',
      ),

      // 8. Winner selection + scaling
      aiNode(
        "8",
        "AI: Select winner & recommend scaling",
        1720,
        "You are a paid media optimization expert. Based on the test campaign data for UGC hooks, identify the winning creative, explain why it outperformed, and provide a scaling budget recommendation. Respond JSON: {winner_hook: string, winner_persona: string, win_reason: string, scale_budget: string, scale_audiences: string, iteration_notes: string}",
        "Product: {product_name}. Hooks tested: {hook_styles}. Personas: {ugc_personas}. Campaign ID: {tiktok_campaign_id}. Script count: {ugc_script_count}.",
        [
          { field: "winner_hook", as: "ugc_winner_hook", type: "string" },
          { field: "winner_persona", as: "ugc_winner_persona", type: "string" },
          { field: "win_reason", as: "ugc_win_reason", type: "string" },
          { field: "scale_budget", as: "ugc_scale_budget", type: "string" },
          {
            field: "scale_audiences",
            as: "ugc_scale_audiences",
            type: "string",
          },
          {
            field: "iteration_notes",
            as: "ugc_iteration_notes",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1014. HOT LEADS AUDIENCE PUSH (6 nodes)
  // ===================================================================

  "Hot Leads Audience Push": {
    summary:
      "Pushes hot leads from HubSpot to Meta Custom Audiences for retargeting and lookalike creation. A scoring trigger fires when leads cross the threshold, lead data is exported from HubSpot, PII is hashed via runCode for privacy compliance, hashed data is uploaded to Meta Custom Audience, a lookalike audience is created, and sync status is confirmed in Sheets.",
    nodes: [
      trigger("Webhook: Lead score threshold crossed", "webhook_app", {
        source: "hubspot",
        event: "lead_score_threshold",
        howItStarts:
          "Fires when a lead's score crosses the hot-lead threshold (e.g., 80+), indicating they are ready for aggressive retargeting via paid ads",
        setup:
          "Configure HubSpot lead scoring workflows. Set up Meta Marketing API with Custom Audience permissions. Prepare Google Sheets for sync logging.",
        inputFields: [],
      }),

      // 2. Lead data export (HubSpot)
      hubspotPostNode(
        "2",
        "Export hot lead data (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hubspot_score","operator":"GTE","value":"80"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","phone","city","state","zip","country"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "results", customName: "hot_leads_data", type: "string" },
          { name: "total", customName: "hot_lead_count", type: "number" },
        ],
      ),

      // 3. PII hashing (runCode)
      n("3", "runCode", "Hash PII for Meta compliance", 520, {
        runCodeDetails: {
          code: `// Hash PII fields using SHA-256 for Meta Custom Audience compliance
const crypto = require('crypto');
const leads = JSON.parse(variables.hot_leads_data || '[]');
const hashed = leads.map(l => {
  const props = l.properties || l;
  return {
    email_hash: crypto.createHash('sha256').update((props.email || '').toLowerCase().trim()).digest('hex'),
    phone_hash: props.phone ? crypto.createHash('sha256').update(props.phone.replace(/\\D/g, '')).digest('hex') : '',
    fn_hash: crypto.createHash('sha256').update((props.firstname || '').toLowerCase().trim()).digest('hex'),
    ln_hash: crypto.createHash('sha256').update((props.lastname || '').toLowerCase().trim()).digest('hex'),
    zip_hash: props.zip ? crypto.createHash('sha256').update(props.zip.trim()).digest('hex') : '',
    country_hash: props.country ? crypto.createHash('sha256').update((props.country || 'US').toLowerCase().trim()).digest('hex') : ''
  };
});
return { hashed_leads: JSON.stringify(hashed), hashed_count: hashed.length, has_phone_data: hashed.some(h => h.phone_hash !== '') };`,
          outputs: [
            { name: "hashed_leads", type: "string" },
            { name: "hashed_count", type: "number" },
            { name: "has_phone_data", type: "boolean" },
          ],
        },
      }),

      // 4. Meta Custom Audience upload
      n("4", "hitApi", "Upload to Meta Custom Audience", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_ad_account_id}/customaudiences",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Hot Leads — Score 80+ — {current_date}" },
            { key: "subtype", value: "CUSTOM" },
            { key: "customer_file_source", value: "USER_PROVIDED_ONLY" },
            {
              key: "schema",
              value:
                '["EMAIL_SHA256","PHONE_SHA256","FN_SHA256","LN_SHA256","ZIP","COUNTRY"]',
            },
            { key: "data", value: "{hashed_leads}" },
          ],
          variables: [
            { name: "id", customName: "custom_audience_id", type: "string" },
            {
              name: "approximate_count",
              customName: "audience_size",
              type: "number",
            },
          ],
        },
      }),

      // 5. Lookalike audience creation
      n("5", "hitApi", "Create lookalike audience (Meta)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_ad_account_id}/customaudiences",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Lookalike — Hot Leads 80+ — 1%" },
            { key: "subtype", value: "LOOKALIKE" },
            { key: "origin_audience_id", value: "{custom_audience_id}" },
            {
              key: "lookalike_spec",
              value: '{"type":"similarity","country":"US","ratio":0.01}',
            },
          ],
          variables: [
            { name: "id", customName: "lookalike_audience_id", type: "string" },
            {
              name: "approximate_count",
              customName: "lookalike_size",
              type: "number",
            },
          ],
        },
      }),

      // 6. Sync confirmation + monitor (Sheets)
      sheetsNode(
        "6",
        "Log audience sync confirmation (Sheets)",
        1240,
        "Hot_Leads_Audience",
        '[["{custom_audience_id}","{lookalike_audience_id}","{hashed_count}","{audience_size}","{lookalike_size}","{hot_lead_count}","synced","=NOW()"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1015. HOW-TO TUTORIAL VIDEO (7 nodes)
  // ===================================================================

  "How-To Tutorial Video": {
    summary:
      "Produces how-to tutorial videos from request to distribution. A webhook captures the tutorial request, a knowledge base search gathers source material, AI writes the tutorial script, the video is rendered, a quality and accuracy review condition gates publication, the video is distributed to hosting platforms, and errors trigger a Slack fallback notification.",
    nodes: [
      trigger("Webhook: Tutorial video request", "webhook_app", {
        source: "internal",
        event: "tutorial_request",
        howItStarts:
          "Fires when a content team member requests a how-to tutorial video with topic, target skill level, and key steps to cover",
        setup:
          "Configure knowledge base API (Notion or Confluence). Set up video rendering API (Creatomate or Synthesia). Connect Slack for error notifications.",
        inputFields: [],
      }),

      // 2. Knowledge base research
      aiNode(
        "2",
        "AI: Extract request & research topic",
        280,
        "You are a technical content researcher. Extract the tutorial topic, target audience, skill level, and key steps from the webhook payload. Then outline the knowledge needed and identify any prerequisites. Respond JSON: {topic: string, target_audience: string, skill_level: string, key_steps: string, prerequisites: string, estimated_duration: string, search_queries: string}",
        "Tutorial request: {webhook_payload}",
        [
          { field: "topic", as: "tutorial_topic", type: "string" },
          { field: "target_audience", as: "tutorial_audience", type: "string" },
          { field: "skill_level", as: "skill_level", type: "string" },
          { field: "key_steps", as: "key_steps", type: "string" },
          { field: "prerequisites", as: "prerequisites", type: "string" },
          {
            field: "estimated_duration",
            as: "estimated_duration",
            type: "string",
          },
          { field: "search_queries", as: "research_queries", type: "string" },
        ],
      ),

      // 3. AI tutorial script
      aiNode(
        "3",
        "AI: Write tutorial video script",
        520,
        "You are a professional tutorial scriptwriter. Write a clear, engaging how-to tutorial script for video production. Include intro hook, step-by-step narration with on-screen directions, transitions between steps, and a strong closing CTA. Adapt language to the skill level. Respond JSON: {script: string, scene_directions: string, on_screen_text: string, intro_hook: string, closing_cta: string, word_count: number}",
        "Topic: {tutorial_topic}. Audience: {tutorial_audience}. Level: {skill_level}. Steps: {key_steps}. Prerequisites: {prerequisites}. Duration: {estimated_duration}.",
        [
          { field: "script", as: "tutorial_script", type: "string" },
          { field: "scene_directions", as: "scene_directions", type: "string" },
          { field: "on_screen_text", as: "on_screen_text", type: "string" },
          { field: "intro_hook", as: "intro_hook", type: "string" },
          { field: "closing_cta", as: "closing_cta", type: "string" },
          { field: "word_count", as: "script_word_count", type: "number" },
        ],
      ),

      // 4. Video rendering
      n("4", "hitApi", "Render tutorial video", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_tutorial_template}" },
            {
              key: "modifications",
              value:
                '{"script":"{tutorial_script}","scene_directions":"{scene_directions}","text_overlays":"{on_screen_text}","intro":"{intro_hook}","outro":"{closing_cta}"}',
            },
          ],
          variables: [
            { name: "id", customName: "render_id", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
            { name: "url", customName: "tutorial_video_url", type: "string" },
          ],
        },
      }),

      // 5. Quality + accuracy review condition
      n("5", "condition", "Video render succeeded?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{render_status}",
              operator: "equals",
              value: "succeeded",
            },
          ],
        },
      }),

      // 6. Video hosting + distribution — True branch
      bufferNode(
        "6",
        "Distribute tutorial video",
        1240,
        "NEW Tutorial: {tutorial_topic}\\n\\n{intro_hook}\\n\\nLevel: {skill_level}\\nDuration: {estimated_duration}\\n\\n{closing_cta}",
        "{tutorial_video_url}",
      ),

      // 7. Error + fallback handler (Slack) — False branch
      slackNode(
        "7",
        "Alert: Tutorial render failed (Slack)",
        1240,
        "Tutorial Video Render FAILED\\n\\nTopic: {tutorial_topic}\\nRender ID: {render_id}\\nStatus: {render_status}\\nScript word count: {script_word_count}\\n\\nPlease investigate and re-trigger manually.",
        350,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1016. HUBSPOT LEAD AUTO-CREATE (7 nodes)
  // ===================================================================

  "HubSpot Lead Auto-Create": {
    summary:
      "Automatically creates enriched leads in HubSpot from inbound submissions. A webhook captures the inbound lead, runCode parses and normalizes the data, HubSpot is searched for duplicates, Clearbit enriches the lead with company data, the contact is created in HubSpot, AI generates a lead score and summary, and the sales team is notified via Slack.",
    nodes: [
      trigger("Webhook: Inbound lead submission", "webhook_app", {
        source: "website",
        event: "lead_submission",
        howItStarts:
          "Fires when a new lead submits a form on the website — captures all form fields regardless of format for downstream normalization",
        setup:
          "Configure website form webhook. Set up HubSpot API access with contact creation permissions. Add Clearbit API key for enrichment. Connect Slack for sales notifications.",
        inputFields: [],
      }),

      // 2. Lead data parsing (runCode)
      n("2", "runCode", "Parse and normalize lead data", 280, {
        runCodeDetails: {
          code: `// Parse webhook payload and normalize lead fields
const payload = JSON.parse(variables.webhook_payload || '{}');
const email = (payload.email || payload.Email || payload.email_address || '').toLowerCase().trim();
const firstName = payload.first_name || payload.firstName || payload.name?.split(' ')[0] || '';
const lastName = payload.last_name || payload.lastName || payload.name?.split(' ').slice(1).join(' ') || '';
const company = payload.company || payload.company_name || payload.organization || '';
const phone = (payload.phone || payload.phone_number || '').replace(/[^+\\d]/g, '');
const source = payload.utm_source || payload.source || payload.referrer || 'website';
const domain = email.split('@')[1] || '';
return { lead_email: email, lead_first_name: firstName, lead_last_name: lastName, lead_company: company, lead_phone: phone, lead_source: source, email_domain: domain };`,
          outputs: [
            { name: "lead_email", type: "string" },
            { name: "lead_first_name", type: "string" },
            { name: "lead_last_name", type: "string" },
            { name: "lead_company", type: "string" },
            { name: "lead_phone", type: "string" },
            { name: "lead_source", type: "string" },
            { name: "email_domain", type: "string" },
          ],
        },
      }),

      // 3. Duplicate detection (HubSpot search)
      hubspotPostNode(
        "3",
        "Check for duplicate contact (HubSpot)",
        520,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{lead_email}"}]}]',
          },
          {
            key: "properties",
            value: '["email","firstname","lastname","hs_object_id"]',
          },
          { key: "limit", value: "1" },
        ],
        [
          { name: "total", customName: "duplicate_count", type: "number" },
          {
            name: "results[0].id",
            customName: "existing_contact_id",
            type: "string",
          },
        ],
      ),

      // 4. Lead enrichment (Clearbit)
      n("4", "hitApi", "Enrich lead with company data (Clearbit)", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={email_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_size",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            { name: "geo.city", customName: "company_city", type: "string" },
            {
              name: "geo.country",
              customName: "company_country",
              type: "string",
            },
          ],
        },
      }),

      // 5. HubSpot contact creation
      hubspotPostNode(
        "5",
        "Create enriched contact (HubSpot)",
        1000,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{lead_first_name}","lastname":"{lead_last_name}","company":"{enriched_company_name}","phone":"{lead_phone}","hs_lead_status":"NEW","lead_source":"{lead_source}","industry":"{company_industry}","numberofemployees":"{company_size}","city":"{company_city}","country":"{company_country}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "created_email",
            type: "string",
          },
          {
            name: "createdAt",
            customName: "contact_created_at",
            type: "string",
          },
        ],
      ),

      // 6. AI lead score + summary
      aiNode(
        "6",
        "AI: Score lead & generate summary",
        1240,
        "You are a sales intelligence analyst. Given the lead and company data, generate a lead score (0-100) and a concise summary for the sales team. Consider company size, industry fit, revenue, and source quality. Respond JSON: {lead_score: number, score_reason: string, summary: string, recommended_action: string, priority: string, ideal_rep_profile: string}",
        "Lead: {lead_first_name} {lead_last_name} ({lead_email}). Company: {enriched_company_name}. Industry: {company_industry}. Size: {company_size}. Revenue: {company_revenue}. Source: {lead_source}. Location: {company_city}, {company_country}.",
        [
          { field: "lead_score", as: "ai_lead_score", type: "number" },
          { field: "score_reason", as: "score_reason", type: "string" },
          { field: "summary", as: "lead_summary", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "priority", as: "lead_priority", type: "string" },
          { field: "ideal_rep_profile", as: "ideal_rep", type: "string" },
        ],
      ),

      // 7. Sales team notification (Slack)
      slackNode(
        "7",
        "Notify sales team (Slack)",
        1480,
        "New Lead Created in HubSpot\\n\\nName: {lead_first_name} {lead_last_name}\\nEmail: {lead_email}\\nCompany: {enriched_company_name} ({company_industry})\\nSize: {company_size} employees | Revenue: {company_revenue}\\n\\nAI Score: {ai_lead_score}/100 — {lead_priority}\\nReason: {score_reason}\\n\\nSummary: {lead_summary}\\nAction: {recommended_action}\\n\\nHubSpot ID: {hubspot_contact_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1017. INCENTIVE-LADDER WINBACK (8 nodes)
  // ===================================================================

  "Incentive-Ladder Winback": {
    summary:
      "Executes a three-stage winback email sequence where each stage escalates the incentive. Scans for lapsed customers, assigns value tiers via runCode, AI crafts a soft-touch first email, waits and checks engagement, sends a second email with a small incentive, waits again and checks, sends a third email with the maximum incentive, and logs the full sequence outcome to Sheets.",
    nodes: [
      trigger("Schedule: Lapsed customer scan", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs every Monday at 8 AM to identify customers who have not purchased in 45+ days and initiate an escalating incentive winback sequence",
        setup:
          "Configure CRM with purchase history and last-order dates. Set up email service for sequence delivery. Prepare Google Sheets for outcome logging.",
        inputFields: [],
      }),

      // 2. Customer value tier assignment (runCode)
      n("2", "runCode", "Assign customer value tiers", 280, {
        runCodeDetails: {
          code: `// Assign incentive tiers based on customer lifetime value and purchase frequency
const ltv = parseFloat(variables.customer_ltv || '0');
const orderCount = parseInt(variables.customer_order_count || '0', 10);
let tier, softTouch, smallIncentive, maxIncentive;
if (ltv >= 500 || orderCount >= 10) {
  tier = 'platinum';
  softTouch = 'We miss you — exclusive preview of new arrivals';
  smallIncentive = '15% off your next order';
  maxIncentive = '25% off + free shipping + bonus gift';
} else if (ltv >= 200 || orderCount >= 5) {
  tier = 'gold';
  softTouch = 'It has been a while — see what is new';
  smallIncentive = '10% off your next order';
  maxIncentive = '20% off + free shipping';
} else {
  tier = 'silver';
  softTouch = 'We have not seen you lately — quick update inside';
  smallIncentive = 'Free shipping on your next order';
  maxIncentive = '15% off + free shipping';
}
return { value_tier: tier, soft_touch_angle: softTouch, small_incentive: smallIncentive, max_incentive: maxIncentive, customer_ltv_bucket: ltv >= 500 ? 'high' : ltv >= 200 ? 'medium' : 'low' };`,
          outputs: [
            { name: "value_tier", type: "string" },
            { name: "soft_touch_angle", type: "string" },
            { name: "small_incentive", type: "string" },
            { name: "max_incentive", type: "string" },
            { name: "customer_ltv_bucket", type: "string" },
          ],
        },
      }),

      // 3. AI email #1 soft touch
      aiNode(
        "3",
        "AI: Craft soft-touch winback email #1",
        520,
        "You are a customer retention email specialist. Write a warm, no-incentive winback email that reconnects with the customer through value and curiosity. No discounts yet — just re-engagement. Respond JSON: {subject: string, body: string, cta: string}",
        "Customer: {customer_email}. Tier: {value_tier}. Angle: {soft_touch_angle}. Days lapsed: {days_since_last_order}.",
        [
          { field: "subject", as: "email1_subject", type: "string" },
          { field: "body", as: "email1_body", type: "string" },
          { field: "cta", as: "email1_cta", type: "string" },
        ],
      ),

      // 4. Engagement gate #1 (delay + condition)
      n("4", "delayAction", "Wait 4 days after email #1", 760, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 4, unit: "days" },
        },
      }),

      // 4b. Condition
      n("4b", "condition", "Opened or clicked email #1?", 940, {
        conditionDetails: {
          conditions: [
            { variable: "{email1_opened}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 5. AI email #2 small incentive — False branch
      aiNode(
        "5",
        "AI: Craft small-incentive email #2",
        1180,
        "You are a customer retention specialist. The first soft-touch email was not opened. Now offer a small incentive to re-engage this lapsed customer. Keep it friendly but add light urgency. Respond JSON: {subject: string, body: string, incentive_code: string, expiry: string}",
        "Customer: {customer_email}. Tier: {value_tier}. Incentive: {small_incentive}. LTV bucket: {customer_ltv_bucket}.",
        [
          { field: "subject", as: "email2_subject", type: "string" },
          { field: "body", as: "email2_body", type: "string" },
          { field: "incentive_code", as: "email2_code", type: "string" },
          { field: "expiry", as: "email2_expiry", type: "string" },
        ],
        350,
      ),

      // 6. Engagement gate #2
      n(
        "6",
        "delayAction",
        "Wait 5 days after email #2",
        1420,
        {
          delayDetails: {
            delayType: "fixed",
            fixedDelay: { value: 5, unit: "days" },
          },
        },
        350,
      ),

      // 6b. Condition
      n(
        "6b",
        "condition",
        "Opened or clicked email #2?",
        1600,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{email2_opened}",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
        350,
      ),

      // 7. AI email #3 maximum incentive — False branch
      aiNode(
        "7",
        "AI: Craft maximum-incentive email #3",
        1840,
        "You are a customer retention specialist. This is the final attempt. The customer ignored both previous emails. Deploy the maximum incentive with strong urgency and a clear expiration. Make it feel like a last chance. Respond JSON: {subject: string, body: string, max_code: string, expiry: string, final_note: string}",
        "Customer: {customer_email}. Tier: {value_tier}. Max incentive: {max_incentive}. Previous codes: {email2_code}.",
        [
          { field: "subject", as: "email3_subject", type: "string" },
          { field: "body", as: "email3_body", type: "string" },
          { field: "max_code", as: "email3_code", type: "string" },
          { field: "expiry", as: "email3_expiry", type: "string" },
          { field: "final_note", as: "email3_final_note", type: "string" },
        ],
        600,
      ),

      // 8. Sequence outcome logger (Sheets)
      sheetsNode(
        "8",
        "Log winback sequence outcome (Sheets)",
        2080,
        "Incentive_Ladder_Winback",
        '[["{customer_email}","{value_tier}","{customer_ltv_bucket}","{email1_subject}","{email2_subject}","{email3_subject}","{email2_code}","{email3_code}","{max_incentive}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4b"),
      e("4b", "8", "output_1"),
      e("4b", "5", "output_2"),
      e("5", "6"),
      e("6", "6b"),
      e("6b", "8", "output_1"),
      e("6b", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1018. INFLUENCER OUTREACH SEQUENCE (8 nodes)
  // ===================================================================

  "Influencer Outreach Sequence": {
    summary:
      "Runs a personalized multi-touch influencer outreach sequence. A webhook triggers from the influencer list, SerpAPI enriches influencer profiles with recent content and audience data, AI crafts a personalized first outreach email, the email is dispatched, a delay and condition monitor for responses, AI generates a follow-up email for non-responders, a final third follow-up is sent, and all outcomes are tracked in Sheets.",
    nodes: [
      trigger("Webhook: Influencer outreach list uploaded", "webhook_app", {
        source: "internal",
        event: "influencer_list_upload",
        howItStarts:
          "Fires when the partnerships team uploads a new batch of influencer prospects, providing names, handles, platforms, and campaign brief",
        setup:
          "Configure SerpAPI for influencer research. Set up email sending service. Prepare Google Sheets for outreach tracking and analytics.",
        inputFields: [],
      }),

      // 2. Influencer research enrichment (SerpAPI)
      n("2", "hitApi", "Research influencer profile (SerpAPI)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?engine=google&q={influencer_name}+{influencer_platform}+influencer&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "influencer_research",
              type: "string",
            },
            {
              name: "organic_results[0].snippet",
              customName: "influencer_bio_snippet",
              type: "string",
            },
            {
              name: "organic_results[0].link",
              customName: "influencer_profile_url",
              type: "string",
            },
            {
              name: "knowledge_graph.description",
              customName: "influencer_description",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI personalized outreach email #1
      aiNode(
        "3",
        "AI: Craft personalized outreach email #1",
        520,
        "You are an influencer partnerships specialist. Write a personalized, genuine outreach email to an influencer. Reference their specific recent content, explain why they are a great fit for the campaign, and make the value proposition clear without being pushy. Respond JSON: {subject: string, body: string, personalization_hook: string, value_prop: string, campaign_brief_summary: string}",
        "Influencer: {influencer_name}. Platform: {influencer_platform}. Bio: {influencer_bio_snippet}. Description: {influencer_description}. Campaign brief: {webhook_payload}.",
        [
          { field: "subject", as: "outreach_subject_1", type: "string" },
          { field: "body", as: "outreach_body_1", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          { field: "value_prop", as: "value_prop", type: "string" },
          {
            field: "campaign_brief_summary",
            as: "campaign_summary",
            type: "string",
          },
        ],
      ),

      // 4. Email dispatch
      emailNode(
        "4",
        "Send outreach email #1",
        760,
        "{influencer_email}",
        "{outreach_subject_1}",
        "{outreach_body_1}",
      ),

      // 5. Response monitor (delay + condition)
      n("5", "delayAction", "Wait 5 days for response", 1000, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 5, unit: "days" },
        },
      }),

      // 5b. Condition
      n("5b", "condition", "Influencer responded?", 1180, {
        conditionDetails: {
          conditions: [
            {
              variable: "{email1_reply_received}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 6. AI follow-up email #2 — False branch
      aiNode(
        "6",
        "AI: Craft follow-up email #2",
        1420,
        "You are an influencer partnerships specialist. The first outreach email received no response. Write a shorter, casual follow-up that adds new value — perhaps share a relevant case study or updated campaign detail. Respond JSON: {subject: string, body: string, new_angle: string}",
        "Influencer: {influencer_name}. Previous subject: {outreach_subject_1}. Personalization: {personalization_hook}. Campaign: {campaign_summary}.",
        [
          { field: "subject", as: "outreach_subject_2", type: "string" },
          { field: "body", as: "outreach_body_2", type: "string" },
          { field: "new_angle", as: "followup_angle", type: "string" },
        ],
        350,
      ),

      // 7. Final follow-up #3
      aiNode(
        "7",
        "AI: Craft final follow-up email #3",
        1660,
        "You are an influencer partnerships specialist. This is the final follow-up. Keep it brief, respectful, and give them an easy way to say yes or no. Add a soft deadline. Respond JSON: {subject: string, body: string, soft_deadline: string}",
        "Influencer: {influencer_name}. Previous subjects: {outreach_subject_1}, {outreach_subject_2}. Follow-up angle: {followup_angle}.",
        [
          { field: "subject", as: "outreach_subject_3", type: "string" },
          { field: "body", as: "outreach_body_3", type: "string" },
          { field: "soft_deadline", as: "final_deadline", type: "string" },
        ],
        350,
      ),

      // 8. Outreach tracker + analytics (Sheets)
      sheetsNode(
        "8",
        "Log outreach sequence (Sheets)",
        1900,
        "Influencer_Outreach",
        '[["{influencer_name}","{influencer_platform}","{influencer_email}","{outreach_subject_1}","{outreach_subject_2}","{outreach_subject_3}","{personalization_hook}","{campaign_summary}","{final_deadline}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "5b"),
      e("5b", "8", "output_1"),
      e("5b", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1019. INSPIRATIONAL QUOTE POST GENERATOR (6 nodes)
  // ===================================================================

  "Inspirational Quote Post Generator": {
    summary:
      "Generates and publishes branded inspirational quote posts on a content calendar schedule. A scheduled trigger fires, AI generates a quote with reflection and hashtags, DALL-E creates a visual quote card, a brand and tone review condition gates publication, approved posts are published via Buffer, and engagement is monitored in Sheets.",
    nodes: [
      trigger("Schedule: Daily inspirational post", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs daily at 7 AM to generate and publish a branded inspirational quote post across social media platforms",
        setup:
          "Configure OpenAI API for quote and DALL-E image generation. Connect Buffer for multi-platform publishing. Set up Google Sheets for engagement tracking.",
        inputFields: [],
      }),

      // 2. AI quote + reflection generation
      aiNode(
        "2",
        "AI: Generate quote & reflection",
        280,
        "You are an inspirational content creator for a professional brand. Generate a unique, thought-provoking quote relevant to business, leadership, or personal growth. Include a brief reflection paragraph that expands on the quote's meaning. Add relevant hashtags. Respond JSON: {quote: string, author_attribution: string, reflection: string, theme: string, hashtags: string, mood: string, visual_direction: string}",
        "Generate a fresh inspirational quote for today's post. Theme rotation: Monday=leadership, Tuesday=resilience, Wednesday=innovation, Thursday=teamwork, Friday=growth, Weekend=balance. Today is {day_of_week}.",
        [
          { field: "quote", as: "quote_text", type: "string" },
          { field: "author_attribution", as: "quote_author", type: "string" },
          { field: "reflection", as: "quote_reflection", type: "string" },
          { field: "theme", as: "quote_theme", type: "string" },
          { field: "hashtags", as: "quote_hashtags", type: "string" },
          { field: "mood", as: "quote_mood", type: "string" },
          { field: "visual_direction", as: "visual_direction", type: "string" },
        ],
      ),

      // 3. Visual quote card creation (DALL-E)
      n("3", "hitApi", "Generate visual quote card (DALL-E)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Create a beautiful, minimalist social media quote card. Visual direction: {visual_direction}. Mood: {quote_mood}. Theme: {quote_theme}. The design should be elegant with clean typography space for overlaying a quote. Use a professional color palette. No text in the image — only abstract/aesthetic background.",
            },
            { key: "size", value: "1024x1024" },
            { key: "quality", value: "hd" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "quote_card_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "dalle_revised_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 4. Brand + tone review condition
      n("4", "condition", "Quote passes brand review?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{quote_mood}",
              operator: "not_equals",
              value: "controversial",
            },
          ],
        },
      }),

      // 5. Multi-platform publisher (Buffer) — True branch
      bufferNode(
        "5",
        "Publish quote post (Buffer)",
        1000,
        '\\"{quote_text}\\"\\n— {quote_author}\\n\\n{quote_reflection}\\n\\n{quote_hashtags}',
        "{quote_card_url}",
      ),

      // 6. Engagement monitor (Sheets)
      sheetsNode(
        "6",
        "Track quote post engagement (Sheets)",
        1240,
        "Quote_Posts",
        '[["{quote_text}","{quote_author}","{quote_theme}","{quote_mood}","{quote_hashtags}","{buffer_update_id}","{quote_card_url}","published","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 1020. INSTAGRAM + FACEBOOK CROSS-POST VIDEO (7 nodes)
  // ===================================================================

  "Instagram + Facebook Cross-Post Video": {
    summary:
      "Creates and cross-posts video content to both Instagram and Facebook. A webhook captures the video brief, AI writes a platform-optimized script, the video is rendered, runCode validates quality and specs for both platforms, an approval gate lets the team review, the video is published cross-platform via Buffer, and performance is tracked in Sheets.",
    nodes: [
      trigger("Webhook: Cross-post video brief", "webhook_app", {
        source: "internal",
        event: "cross_post_video_brief",
        howItStarts:
          "Fires when a social media manager submits a video brief for cross-posting to Instagram Reels and Facebook Reels/Feed",
        setup:
          "Configure video rendering API. Set up Buffer with linked Instagram and Facebook profiles. Connect Google Sheets for performance tracking.",
        inputFields: [],
      }),

      // 2. AI video script
      aiNode(
        "2",
        "AI: Write cross-platform video script",
        280,
        "You are a social video strategist. Write a video script optimized for both Instagram Reels and Facebook Reels. Account for differences in audience behavior and algorithmic preferences. Include hook, body, and CTA. Respond JSON: {script: string, hook_line: string, cta: string, ig_caption: string, fb_caption: string, hashtags_ig: string, hashtags_fb: string, duration_sec: number, topic: string}",
        "Video brief: {webhook_payload}",
        [
          { field: "script", as: "video_script", type: "string" },
          { field: "hook_line", as: "video_hook", type: "string" },
          { field: "cta", as: "video_cta", type: "string" },
          { field: "ig_caption", as: "ig_caption", type: "string" },
          { field: "fb_caption", as: "fb_caption", type: "string" },
          { field: "hashtags_ig", as: "ig_hashtags", type: "string" },
          { field: "hashtags_fb", as: "fb_hashtags", type: "string" },
          { field: "duration_sec", as: "target_duration", type: "number" },
          { field: "topic", as: "video_topic", type: "string" },
        ],
      ),

      // 3. Video rendering
      n("3", "hitApi", "Render cross-platform video", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_reels_template}" },
            {
              key: "modifications",
              value:
                '{"script":"{video_script}","hook":"{video_hook}","cta":"{video_cta}","duration":"{target_duration}"}',
            },
          ],
          variables: [
            { name: "id", customName: "render_id", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
            { name: "url", customName: "video_url", type: "string" },
          ],
        },
      }),

      // 4. Quality + spec validation (runCode)
      n("4", "runCode", "Validate video specs for IG + FB", 760, {
        runCodeDetails: {
          code: `// Validate video meets both Instagram and Facebook specs
const status = variables.render_status;
const duration = parseInt(variables.target_duration || '0', 10);
const igValid = duration >= 3 && duration <= 90; // IG Reels max 90s
const fbValid = duration >= 3 && duration <= 240; // FB Reels max 240s
const renderOk = status === 'succeeded';
const allValid = renderOk && igValid && fbValid;
return { ig_spec_valid: igValid, fb_spec_valid: fbValid, render_ok: renderOk, all_specs_valid: allValid, validation_notes: allValid ? 'All specs met' : 'Check duration: IG=' + igValid + ' FB=' + fbValid + ' Render=' + renderOk };`,
          outputs: [
            { name: "ig_spec_valid", type: "boolean" },
            { name: "fb_spec_valid", type: "boolean" },
            { name: "render_ok", type: "boolean" },
            { name: "all_specs_valid", type: "boolean" },
            { name: "validation_notes", type: "string" },
          ],
        },
      }),

      // 5. Approval gate
      n("5", "approvalGate", "Team approval for cross-post", 1000, {
        approvalGateDetails: {
          approvers: ["{social_media_manager_email}"],
          message:
            "Cross-post video ready for review.\\n\\nTopic: {video_topic}\\nDuration: {target_duration}s\\nIG Spec: {ig_spec_valid} | FB Spec: {fb_spec_valid}\\nValidation: {validation_notes}\\n\\nIG Caption: {ig_caption}\\nFB Caption: {fb_caption}\\n\\nVideo: {video_url}",
          requiredApprovals: 1,
        },
      }),

      // 6. Cross-platform publisher (Buffer)
      bufferNode(
        "6",
        "Publish to Instagram + Facebook (Buffer)",
        1240,
        "{ig_caption}\\n\\n{ig_hashtags}",
        "{video_url}",
      ),

      // 7. Cross-post performance tracker (Sheets)
      sheetsNode(
        "7",
        "Track cross-post performance (Sheets)",
        1480,
        "CrossPost_Videos",
        '[["{video_topic}","{target_duration}","{ig_caption}","{fb_caption}","{ig_hashtags}","{video_url}","{buffer_update_id}","{validation_notes}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1021. INSTAGRAM CONTENT CALENDAR (7 nodes)
  // ===================================================================

  "Instagram Content Calendar": {
    summary:
      "Plans and schedules a full week of Instagram content. A weekly trigger fires, SerpAPI researches trending topics and competitor content, AI generates content ideas for the week, AI writes captions and hashtags for each post, DALL-E creates visual assets, an approval gate lets the team review the calendar, and approved posts are scheduled via Buffer.",
    nodes: [
      trigger("Schedule: Weekly Instagram content planning", "schedule", {
        schedule: "0 9 * * 0",
        howItStarts:
          "Runs every Sunday at 9 AM to plan, create, and schedule the full week of Instagram content including visuals, captions, and hashtags",
        setup:
          "Configure SerpAPI for trend research. Set up OpenAI for content generation and DALL-E for visuals. Connect Buffer for scheduling. Add approval gate reviewers.",
        inputFields: [],
      }),

      // 2. Trend + performance research (SerpAPI)
      n("2", "hitApi", "Research trends & competitor content (SerpAPI)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?engine=google_trends&q={brand_niche}+instagram+trends&data_type=TIMESERIES&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "interest_over_time.timeline_data",
              customName: "trend_data",
              type: "string",
            },
            {
              name: "related_queries.rising",
              customName: "rising_queries",
              type: "string",
            },
            {
              name: "related_topics.top",
              customName: "top_topics",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI content ideation
      aiNode(
        "3",
        "AI: Generate weekly content ideas",
        520,
        "You are an Instagram content strategist. Based on current trends and the brand niche, generate 7 content ideas (one per day) with a mix of formats: carousels, Reels, static posts, Stories. Include the content pillar each maps to. Respond JSON: {ideas: string, content_mix: string, pillar_distribution: string, weekly_theme: string, top_trend_leveraged: string}",
        "Brand niche: {brand_niche}. Trend data: {trend_data}. Rising queries: {rising_queries}. Top topics: {top_topics}.",
        [
          { field: "ideas", as: "content_ideas", type: "string" },
          { field: "content_mix", as: "content_mix", type: "string" },
          {
            field: "pillar_distribution",
            as: "pillar_distribution",
            type: "string",
          },
          { field: "weekly_theme", as: "weekly_theme", type: "string" },
          { field: "top_trend_leveraged", as: "top_trend", type: "string" },
        ],
      ),

      // 4. AI caption + hashtag writer
      aiNode(
        "4",
        "AI: Write captions & hashtags for each post",
        760,
        "You are an Instagram copywriter. For each of the 7 content ideas, write an engaging caption with a hook, value, and CTA. Generate a custom hashtag set (20-30 per post) mixing popular, niche, and branded hashtags. Respond JSON: {captions: string, hashtag_sets: string, best_posting_times: string, engagement_hooks: string}",
        "Content ideas: {content_ideas}. Weekly theme: {weekly_theme}. Content mix: {content_mix}. Brand niche: {brand_niche}.",
        [
          { field: "captions", as: "post_captions", type: "string" },
          { field: "hashtag_sets", as: "post_hashtags", type: "string" },
          { field: "best_posting_times", as: "posting_times", type: "string" },
          { field: "engagement_hooks", as: "engagement_hooks", type: "string" },
        ],
      ),

      // 5. Visual asset creator (DALL-E)
      n("5", "hitApi", "Generate visual assets (DALL-E)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Create a cohesive set of Instagram visual assets for a {brand_niche} brand. Weekly theme: {weekly_theme}. Style: modern, clean, on-brand. Content ideas to visualize: {content_ideas}. Create aesthetic, scroll-stopping imagery. No text in images.",
            },
            { key: "size", value: "1024x1024" },
            { key: "quality", value: "hd" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "visual_assets_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "dalle_prompt_used",
              type: "string",
            },
          ],
        },
      }),

      // 6. Calendar review approval gate
      n("6", "approvalGate", "Review weekly content calendar", 1240, {
        approvalGateDetails: {
          approvers: ["{content_lead_email}", "{brand_manager_email}"],
          message:
            "Instagram Content Calendar for the week is ready.\\n\\nWeekly Theme: {weekly_theme}\\nContent Mix: {content_mix}\\nPillar Distribution: {pillar_distribution}\\nTrend Leveraged: {top_trend}\\n\\nCaptions: {post_captions}\\nHashtags: {post_hashtags}\\nPosting Times: {posting_times}\\nVisual Preview: {visual_assets_url}",
          requiredApprovals: 1,
        },
      }),

      // 7. Scheduled publisher (Buffer)
      bufferNode(
        "7",
        "Schedule weekly posts (Buffer)",
        1480,
        "{post_captions}\\n\\n{post_hashtags}",
        "{visual_assets_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1022. INSTAGRAM CONTENT CALENDAR POST (7 nodes)
  // ===================================================================

  "Instagram Content Calendar Post": {
    summary:
      "Executes individual scheduled posts from the Instagram content calendar. A scheduled trigger fires at the designated post time, the content brief is fetched, AI composes the full Instagram post with caption and hashtags, DALL-E generates the visual, an approval gate ensures quality, the post is published to Instagram, and post-publish metrics are logged to Sheets.",
    nodes: [
      trigger("Schedule: Publish calendar post", "schedule", {
        schedule: "0 12 * * *",
        howItStarts:
          "Fires at the scheduled time to compose, generate visuals for, and publish today's Instagram content calendar post",
        setup:
          "Configure content calendar data source. Set up OpenAI and DALL-E APIs. Connect Instagram via Buffer. Set up Google Sheets for post-publish monitoring.",
        inputFields: [],
      }),

      // 2. Content brief fetch
      n("2", "hitApi", "Fetch today's content brief (Sheets)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Content_Calendar!A:G?majorDimension=ROWS",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "calendar_data", type: "string" },
            { name: "values[0]", customName: "today_brief", type: "string" },
          ],
        },
      }),

      // 3. AI Instagram post composer
      aiNode(
        "3",
        "AI: Compose Instagram post",
        520,
        "You are an Instagram content creator. Based on the content brief, write a complete Instagram post: an attention-grabbing first line (the hook), value-packed body, clear CTA, and optimized hashtags. Make it feel native to Instagram. Respond JSON: {caption: string, first_line_hook: string, cta: string, hashtags: string, alt_text: string, content_type: string, visual_prompt: string}",
        "Today's brief: {today_brief}. Calendar context: {calendar_data}.",
        [
          { field: "caption", as: "ig_caption", type: "string" },
          { field: "first_line_hook", as: "ig_hook", type: "string" },
          { field: "cta", as: "ig_cta", type: "string" },
          { field: "hashtags", as: "ig_hashtags", type: "string" },
          { field: "alt_text", as: "ig_alt_text", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "visual_prompt", as: "visual_prompt", type: "string" },
        ],
      ),

      // 4. Visual asset generator (DALL-E)
      n("4", "hitApi", "Generate post visual (DALL-E)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "{visual_prompt}. Style: professional Instagram aesthetic, vibrant, scroll-stopping. No text in image.",
            },
            { key: "size", value: "1024x1024" },
            { key: "quality", value: "hd" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "post_image_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "dalle_final_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 5. Approval gate
      n("5", "approvalGate", "Approve post before publishing", 1000, {
        approvalGateDetails: {
          approvers: ["{social_media_manager_email}"],
          message:
            "Instagram post ready for publishing.\\n\\nHook: {ig_hook}\\nCaption: {ig_caption}\\nCTA: {ig_cta}\\nHashtags: {ig_hashtags}\\nType: {content_type}\\nImage: {post_image_url}",
          requiredApprovals: 1,
        },
      }),

      // 6. Instagram publisher
      bufferNode(
        "6",
        "Publish to Instagram (Buffer)",
        1240,
        "{ig_caption}\\n\\n{ig_hashtags}",
        "{post_image_url}",
      ),

      // 7. Post-publish monitor (Sheets)
      sheetsNode(
        "7",
        "Log post-publish metrics (Sheets)",
        1480,
        "IG_Post_Log",
        '[["{ig_hook}","{content_type}","{ig_caption}","{ig_hashtags}","{post_image_url}","{buffer_update_id}","published","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1023. INTERNATIONAL SHOPPER WELCOME (7 nodes)
  // ===================================================================

  "International Shopper Welcome": {
    summary:
      "Delivers a localized welcome experience for international signups. A webhook fires on international signup detection, runCode performs geo and locale detection, regional content is assembled based on location, AI writes a localized welcome email, the email is dispatched, the CRM profile is updated with locale data in HubSpot, and a fallback handler catches errors.",
    nodes: [
      trigger("Webhook: International signup detected", "webhook_app", {
        source: "website",
        event: "international_signup",
        howItStarts:
          "Fires when a new user signs up from a non-domestic IP address or selects a non-default locale, indicating an international shopper",
        setup:
          "Configure website signup webhook with IP/locale data. Set up HubSpot for contact updates with locale fields. Prepare localized email templates.",
        inputFields: [],
      }),

      // 2. Geo + locale detection (runCode)
      n("2", "runCode", "Detect geo location & locale preferences", 280, {
        runCodeDetails: {
          code: `// Parse webhook payload and determine geo/locale information
const payload = JSON.parse(variables.webhook_payload || '{}');
const email = (payload.email || '').toLowerCase().trim();
const name = payload.name || payload.first_name || 'Valued Customer';
const ip = payload.ip_address || payload.ip || '';
const locale = payload.locale || payload.language || 'en-US';
const country = payload.country || payload.geo_country || 'US';
const currency = { US: 'USD', GB: 'GBP', DE: 'EUR', FR: 'EUR', JP: 'JPY', AU: 'AUD', CA: 'CAD', BR: 'BRL', MX: 'MXN', IN: 'INR' }[country] || 'USD';
const language = locale.split('-')[0] || 'en';
const region = { US: 'North America', CA: 'North America', GB: 'Europe', DE: 'Europe', FR: 'Europe', JP: 'Asia Pacific', AU: 'Asia Pacific', BR: 'Latin America', MX: 'Latin America', IN: 'Asia Pacific' }[country] || 'International';
return { customer_email: email, customer_name: name, detected_country: country, detected_currency: currency, detected_language: language, detected_region: region, locale_code: locale };`,
          outputs: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "detected_country", type: "string" },
            { name: "detected_currency", type: "string" },
            { name: "detected_language", type: "string" },
            { name: "detected_region", type: "string" },
            { name: "locale_code", type: "string" },
          ],
        },
      }),

      // 3. Regional content assembly
      aiNode(
        "3",
        "AI: Assemble regional content & offers",
        520,
        "You are an international ecommerce specialist. Based on the detected country, currency, and region, assemble region-specific content including shipping information, payment methods available, local promotions, and cultural considerations. Respond JSON: {shipping_info: string, payment_methods: string, local_promotion: string, cultural_greeting: string, support_hours: string, local_social_proof: string}",
        "Country: {detected_country}. Currency: {detected_currency}. Language: {detected_language}. Region: {detected_region}.",
        [
          { field: "shipping_info", as: "regional_shipping", type: "string" },
          { field: "payment_methods", as: "regional_payments", type: "string" },
          { field: "local_promotion", as: "regional_promo", type: "string" },
          {
            field: "cultural_greeting",
            as: "cultural_greeting",
            type: "string",
          },
          {
            field: "support_hours",
            as: "regional_support_hours",
            type: "string",
          },
          {
            field: "local_social_proof",
            as: "local_social_proof",
            type: "string",
          },
        ],
      ),

      // 4. AI localized welcome
      aiNode(
        "4",
        "AI: Write localized welcome email",
        760,
        "You are a multilingual email marketing specialist. Write a warm, localized welcome email for an international customer. Use the cultural greeting, reference local shipping and payment options, include a regional promotion, and make the customer feel that the brand understands their market. Write primarily in English but include the cultural greeting in local language. Respond JSON: {subject: string, body: string, preview_text: string}",
        "Customer: {customer_name}. Country: {detected_country}. Currency: {detected_currency}. Greeting: {cultural_greeting}. Shipping: {regional_shipping}. Payments: {regional_payments}. Promo: {regional_promo}. Social proof: {local_social_proof}. Support: {regional_support_hours}.",
        [
          { field: "subject", as: "welcome_subject", type: "string" },
          { field: "body", as: "welcome_body", type: "string" },
          { field: "preview_text", as: "welcome_preview", type: "string" },
        ],
      ),

      // 5. Welcome email dispatch
      emailNode(
        "5",
        "Send localized welcome email",
        1000,
        "{customer_email}",
        "{welcome_subject}",
        "{welcome_body}",
      ),

      // 6. CRM profile update (HubSpot)
      hubspotPostNode(
        "6",
        "Update CRM profile with locale (HubSpot)",
        1240,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{customer_email}","firstname":"{customer_name}","country":"{detected_country}","hs_language":"{detected_language}","preferred_currency":"{detected_currency}","region":"{detected_region}","locale":"{locale_code}","lead_source":"international_signup"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_email",
            type: "string",
          },
        ],
      ),

      // 7. Fallback + error handler
      n("7", "runCode", "Handle errors & log fallback", 1480, {
        runCodeDetails: {
          code: `// Log the international welcome outcome and flag any issues
const contactId = variables.hubspot_contact_id || 'FAILED';
const emailSent = variables.welcome_subject ? true : false;
const hasErrors = contactId === 'FAILED' || !emailSent;
const status = hasErrors ? 'error' : 'success';
const errorDetails = [];
if (contactId === 'FAILED') errorDetails.push('HubSpot contact creation failed');
if (!emailSent) errorDetails.push('Welcome email not generated');
return { welcome_status: status, error_details: errorDetails.join('; ') || 'none', hubspot_id_final: contactId, welcome_completed: !hasErrors };`,
          outputs: [
            { name: "welcome_status", type: "string" },
            { name: "error_details", type: "string" },
            { name: "hubspot_id_final", type: "string" },
            { name: "welcome_completed", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1024. INVALID OR ABSENT PHONE CLEANUP (6 nodes)
  // ===================================================================

  "Invalid or Absent Phone Cleanup": {
    summary:
      "Cleans up invalid or missing phone numbers by collecting updated information via email. An SMS failure event triggers the flow, runCode classifies the failure type, AI writes a phone collection email, the email is dispatched, HubSpot is updated with the new phone or flagged for manual review, and the cleanup outcome is logged to Sheets.",
    nodes: [
      trigger("Webhook: SMS delivery failure detected", "webhook_app", {
        source: "twilio",
        event: "sms_delivery_failure",
        howItStarts:
          "Fires when Twilio reports an SMS delivery failure due to an invalid, disconnected, or absent phone number",
        setup:
          "Configure Twilio status callback webhook. Set up HubSpot for phone field updates. Prepare email templates for phone collection. Connect Google Sheets for cleanup logging.",
        inputFields: [],
      }),

      // 2. Failure classification (runCode)
      n("2", "runCode", "Classify SMS failure & extract contact", 280, {
        runCodeDetails: {
          code: `// Parse the SMS failure webhook and classify the error type
const payload = JSON.parse(variables.webhook_payload || '{}');
const errorCode = payload.ErrorCode || payload.error_code || '';
const to = payload.To || payload.to || '';
const contactEmail = payload.contact_email || payload.email || '';
const contactName = payload.contact_name || payload.name || 'Customer';
let failureType, recoverable;
if (['30003', '30005', '30006'].includes(String(errorCode))) {
  failureType = 'unreachable'; recoverable = true;
} else if (['21211', '21214', '21217'].includes(String(errorCode))) {
  failureType = 'invalid_number'; recoverable = true;
} else if (['30004', '30007'].includes(String(errorCode))) {
  failureType = 'blocked'; recoverable = false;
} else {
  failureType = 'unknown'; recoverable = true;
}
return { failed_phone: to, contact_email: contactEmail, contact_name: contactName, failure_type: failureType, error_code: String(errorCode), is_recoverable: recoverable };`,
          outputs: [
            { name: "failed_phone", type: "string" },
            { name: "contact_email", type: "string" },
            { name: "contact_name", type: "string" },
            { name: "failure_type", type: "string" },
            { name: "error_code", type: "string" },
            { name: "is_recoverable", type: "boolean" },
          ],
        },
      }),

      // 3. AI phone collection email
      aiNode(
        "3",
        "AI: Craft phone collection email",
        520,
        "You are a customer communications specialist. Write a friendly, non-alarming email asking the customer to update their phone number. Explain that we tried to reach them via SMS but could not get through. Make it easy for them to update. Respond JSON: {subject: string, body: string, update_link_text: string}",
        "Customer: {contact_name}. Failed phone: {failed_phone}. Failure type: {failure_type}. We need to collect an updated phone number via email.",
        [
          { field: "subject", as: "phone_email_subject", type: "string" },
          { field: "body", as: "phone_email_body", type: "string" },
          { field: "update_link_text", as: "update_cta", type: "string" },
        ],
      ),

      // 4. Collection email dispatch
      emailNode(
        "4",
        "Send phone collection email",
        760,
        "{contact_email}",
        "{phone_email_subject}",
        "{phone_email_body}",
      ),

      // 5. Phone update handler (HubSpot)
      hubspotPostNode(
        "5",
        "Flag contact for phone update (HubSpot)",
        1000,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{contact_email}"}]}]',
          },
          {
            key: "properties",
            value: '["email","phone","firstname","hs_object_id"]',
          },
          { key: "limit", value: "1" },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "current_phone",
            type: "string",
          },
          { name: "total", customName: "contact_found", type: "number" },
        ],
      ),

      // 6. Cleanup logger (Sheets)
      sheetsNode(
        "6",
        "Log phone cleanup action (Sheets)",
        1240,
        "Phone_Cleanup",
        '[["{contact_email}","{contact_name}","{failed_phone}","{failure_type}","{error_code}","{is_recoverable}","{hubspot_contact_id}","{phone_email_subject}","pending_update","=NOW()"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1025. KLAVIYO BIRTHDAY CAMPAIGN (7 nodes)
  // ===================================================================

  "Klaviyo Birthday Campaign": {
    summary:
      "Sends personalized birthday emails with discount codes on the customer's birthday. A daily schedule checks for upcoming birthdays, HubSpot enriches the customer profile, AI writes a personalized birthday email, runCode generates a unique discount code, the birthday email is dispatched, a delay and condition track redemption with a reminder, and campaign performance is logged to Sheets.",
    nodes: [
      trigger("Schedule: Daily birthday check", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs daily at 6 AM to identify customers whose birthday is today and trigger personalized birthday email campaigns",
        setup:
          "Configure HubSpot with birthday date fields. Set up discount code generation logic. Prepare Google Sheets for campaign performance tracking.",
        inputFields: [],
      }),

      // 2. Customer profile enrichment (HubSpot)
      hubspotPostNode(
        "2",
        "Fetch birthday contacts (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"date_of_birth_month","operator":"EQ","value":"{current_month}"},{"propertyName":"date_of_birth_day","operator":"EQ","value":"{current_day}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","date_of_birth","total_revenue","hs_object_id","favorite_category","lifecyclestage"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          { name: "results", customName: "birthday_contacts", type: "string" },
          { name: "total", customName: "birthday_count", type: "number" },
          {
            name: "results[0].properties.email",
            customName: "birthday_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "birthday_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.total_revenue",
            customName: "customer_ltv",
            type: "string",
          },
          {
            name: "results[0].properties.favorite_category",
            customName: "favorite_category",
            type: "string",
          },
        ],
      ),

      // 3. AI birthday email
      aiNode(
        "3",
        "AI: Write personalized birthday email",
        520,
        "You are a customer celebration email specialist. Write a warm, joyful birthday email that feels personal and special. Reference the customer's name, favorite category, and include the birthday discount. Make it celebratory without being over-the-top. Respond JSON: {subject: string, body: string, personal_note: string, product_recommendation: string}",
        "Customer: {birthday_first_name}. Favorite category: {favorite_category}. LTV: ${customer_ltv}. Today is their birthday!",
        [
          { field: "subject", as: "birthday_subject", type: "string" },
          { field: "body", as: "birthday_body", type: "string" },
          { field: "personal_note", as: "personal_note", type: "string" },
          {
            field: "product_recommendation",
            as: "product_rec",
            type: "string",
          },
        ],
      ),

      // 4. Birthday discount generation (runCode)
      n("4", "runCode", "Generate unique birthday discount code", 760, {
        runCodeDetails: {
          code: `// Generate a unique birthday discount code with expiry
const firstName = (variables.birthday_first_name || 'FRIEND').toUpperCase().substring(0, 4);
const datePart = new Date().toISOString().slice(5, 10).replace('-', '');
const random = Math.random().toString(36).substring(2, 6).toUpperCase();
const code = 'BDAY-' + firstName + '-' + datePart + '-' + random;
const ltv = parseFloat(variables.customer_ltv || '0');
const discountPct = ltv >= 500 ? 25 : ltv >= 200 ? 20 : 15;
const expiryDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
return { discount_code: code, discount_percent: discountPct, code_expiry: expiryDate, is_premium_tier: ltv >= 500 };`,
          outputs: [
            { name: "discount_code", type: "string" },
            { name: "discount_percent", type: "number" },
            { name: "code_expiry", type: "string" },
            { name: "is_premium_tier", type: "boolean" },
          ],
        },
      }),

      // 5. Birthday email dispatch
      emailNode(
        "5",
        "Send birthday email with discount",
        1000,
        "{birthday_email}",
        "{birthday_subject}",
        "{birthday_body}\\n\\nYour exclusive birthday gift: {discount_percent}% off with code {discount_code}\\nValid until: {code_expiry}\\n\\n{personal_note}\\n\\nRecommended for you: {product_rec}",
      ),

      // 6. Redemption reminder (delay + condition)
      n("6", "delayAction", "Wait 5 days for redemption", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 5, unit: "days" },
        },
      }),

      // 6b. Condition
      n("6b", "condition", "Discount code redeemed?", 1420, {
        conditionDetails: {
          conditions: [
            {
              variable: "{discount_redeemed}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 7. Campaign performance logger (Sheets)
      sheetsNode(
        "7",
        "Log birthday campaign performance (Sheets)",
        1660,
        "Birthday_Campaigns",
        '[["{birthday_email}","{birthday_first_name}","{discount_code}","{discount_percent}","{code_expiry}","{customer_ltv}","{favorite_category}","{birthday_count}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "6b"),
      e("6b", "7", "output_1"),
      e("6b", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1026. KLAVIYO BROWSE ABANDONMENT FLOW (7 nodes)
  // ===================================================================

  "Klaviyo Browse Abandonment Flow": {
    summary:
      "Re-engages shoppers who browsed products but did not add to cart or purchase. A browse event triggers the flow, runCode aggregates recent browse history, a condition checks whether the customer already purchased or has items in cart, AI writes a browse abandonment email with product recommendations, email #1 is dispatched, a delayed email #2 offers extended recommendations, and flow performance is monitored in Sheets.",
    nodes: [
      trigger("Webhook: Browse event detected", "webhook_app", {
        source: "klaviyo",
        event: "viewed_product",
        howItStarts:
          "Fires when a customer views a product page without adding to cart, after a debounce period of 30 minutes of browse inactivity",
        setup:
          "Configure Klaviyo webhook for Viewed Product events. Set up product catalog API for recommendation data. Prepare Google Sheets for flow monitoring.",
        inputFields: [],
      }),

      // 2. Browse history aggregation (runCode)
      n("2", "runCode", "Aggregate recent browse history", 280, {
        runCodeDetails: {
          code: `// Parse browse event and aggregate recent product views
const payload = JSON.parse(variables.webhook_payload || '{}');
const email = payload.email || payload.customer_email || '';
const productId = payload.product_id || payload.item_id || '';
const productName = payload.product_name || payload.item_name || '';
const productUrl = payload.product_url || payload.url || '';
const productPrice = payload.price || payload.product_price || '0';
const productImage = payload.image_url || payload.product_image || '';
const category = payload.category || payload.product_category || '';
const browseHistory = payload.recent_views || [{ id: productId, name: productName, price: productPrice, url: productUrl }];
const hasCartItems = payload.cart_items_count > 0;
const hasPurchased = payload.has_recent_purchase === true;
return { customer_email: email, product_id: productId, product_name: productName, product_url: productUrl, product_price: productPrice, product_image: productImage, product_category: category, browse_history: JSON.stringify(browseHistory), has_cart_items: hasCartItems, has_purchased: hasPurchased, browse_count: browseHistory.length };`,
          outputs: [
            { name: "customer_email", type: "string" },
            { name: "product_id", type: "string" },
            { name: "product_name", type: "string" },
            { name: "product_url", type: "string" },
            { name: "product_price", type: "string" },
            { name: "product_image", type: "string" },
            { name: "product_category", type: "string" },
            { name: "browse_history", type: "string" },
            { name: "has_cart_items", type: "boolean" },
            { name: "has_purchased", type: "boolean" },
            { name: "browse_count", type: "number" },
          ],
        },
      }),

      // 3. Purchase + cart check condition
      n("3", "condition", "No purchase or cart items?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{has_purchased}", operator: "equals", value: "false" },
          ],
        },
      }),

      // 4. AI browse abandonment email — True branch (no purchase)
      aiNode(
        "4",
        "AI: Write browse abandonment email #1",
        760,
        "You are an ecommerce email specialist. Write a browse abandonment email that reminds the customer of the products they viewed and provides compelling reasons to return. Include product-specific benefits and social proof. Do not be pushy. Respond JSON: {subject: string, body: string, product_highlight: string, social_proof: string, recommended_similar: string}",
        "Customer: {customer_email}. Viewed: {product_name} (${product_price}). Category: {product_category}. Browse history: {browse_history}. Items browsed: {browse_count}.",
        [
          { field: "subject", as: "abandon_subject_1", type: "string" },
          { field: "body", as: "abandon_body_1", type: "string" },
          {
            field: "product_highlight",
            as: "product_highlight",
            type: "string",
          },
          { field: "social_proof", as: "product_social_proof", type: "string" },
          {
            field: "recommended_similar",
            as: "similar_products",
            type: "string",
          },
        ],
      ),

      // 5. Email #1 dispatch
      emailNode(
        "5",
        "Send browse abandonment email #1",
        1000,
        "{customer_email}",
        "{abandon_subject_1}",
        "{abandon_body_1}",
      ),

      // 6. Email #2 extended recommendations (delay)
      n("6", "delayAction", "Wait 2 days for email #2", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 2, unit: "days" },
        },
      }),

      // 6b. Extended recommendations email
      aiNode(
        "6b",
        "AI: Write extended recommendation email #2",
        1420,
        "You are an ecommerce recommendation specialist. The customer did not respond to the first browse abandonment email. Write a second email focused on curated recommendations based on their browse history. Include category bestsellers and trending items. Respond JSON: {subject: string, body: string, curated_picks: string, trending_note: string}",
        "Customer: {customer_email}. Originally viewed: {product_name}. Category: {product_category}. Browse history: {browse_history}. Similar products from email #1: {similar_products}.",
        [
          { field: "subject", as: "abandon_subject_2", type: "string" },
          { field: "body", as: "abandon_body_2", type: "string" },
          { field: "curated_picks", as: "curated_picks", type: "string" },
          { field: "trending_note", as: "trending_note", type: "string" },
        ],
      ),

      // 7. Flow performance monitor (Sheets)
      sheetsNode(
        "7",
        "Monitor flow performance (Sheets)",
        1660,
        "Browse_Abandonment",
        '[["{customer_email}","{product_name}","{product_price}","{product_category}","{browse_count}","{abandon_subject_1}","{abandon_subject_2}","{similar_products}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "6b"),
      e("6b", "7"),
    ],
  },

  // ===================================================================
  // 1027. KLAVIYO PRODUCT REVIEW REQUEST (7 nodes)
  // ===================================================================

  "Klaviyo Product Review Request": {
    summary:
      "Requests product reviews at the optimal time after delivery confirmation. A delivery event triggers the flow, runCode calculates the ideal review timing based on product type, AI writes a personalized review request email, the request is dispatched, a follow-up incentive offer is sent, a condition checks for review submission, and non-reviewers receive a delayed gentle reminder.",
    nodes: [
      trigger("Webhook: Delivery confirmation received", "webhook_app", {
        source: "klaviyo",
        event: "fulfilled_order",
        howItStarts:
          "Fires when an order is marked as delivered, indicating the customer has received their product and a review request should be scheduled",
        setup:
          "Configure Klaviyo webhook for Fulfilled Order events. Set up review platform API (e.g., Yotpo, Judge.me). Prepare incentive codes for review submissions.",
        inputFields: [],
      }),

      // 2. Review timing calculator (runCode)
      n("2", "runCode", "Calculate optimal review timing", 280, {
        runCodeDetails: {
          code: `// Determine optimal timing for review request based on product type and delivery
const payload = JSON.parse(variables.webhook_payload || '{}');
const email = payload.email || payload.customer_email || '';
const firstName = payload.first_name || payload.customer_name || 'Customer';
const productName = payload.product_name || payload.items?.[0]?.name || 'your recent purchase';
const productId = payload.product_id || payload.items?.[0]?.id || '';
const orderNumber = payload.order_id || payload.order_number || '';
const category = payload.category || payload.product_category || 'general';
// Timing: consumables need less time, electronics/furniture need more
const timingDays = { consumable: 3, apparel: 5, electronics: 7, furniture: 14, beauty: 5, general: 5 };
const waitDays = timingDays[category] || 5;
const reviewUrl = payload.review_url || 'https://store.com/review/' + productId;
return { customer_email: email, customer_first_name: firstName, product_name: productName, product_id: productId, order_number: orderNumber, product_category: category, wait_days: waitDays, review_url: reviewUrl };`,
          outputs: [
            { name: "customer_email", type: "string" },
            { name: "customer_first_name", type: "string" },
            { name: "product_name", type: "string" },
            { name: "product_id", type: "string" },
            { name: "order_number", type: "string" },
            { name: "product_category", type: "string" },
            { name: "wait_days", type: "number" },
            { name: "review_url", type: "string" },
          ],
        },
      }),

      // 3. AI review request email
      aiNode(
        "3",
        "AI: Write review request email",
        520,
        "You are a customer experience specialist. Write a friendly, genuine review request email. Reference the specific product, make it easy to leave a review, and explain how reviews help other customers. Keep it short and appreciative. Respond JSON: {subject: string, body: string, review_prompt: string, star_rating_ask: string}",
        "Customer: {customer_first_name}. Product: {product_name}. Category: {product_category}. Order: {order_number}. Review URL: {review_url}. Days since delivery: {wait_days}.",
        [
          { field: "subject", as: "review_subject", type: "string" },
          { field: "body", as: "review_body", type: "string" },
          { field: "review_prompt", as: "review_prompt", type: "string" },
          { field: "star_rating_ask", as: "star_rating_ask", type: "string" },
        ],
      ),

      // 4. Review request dispatch
      emailNode(
        "4",
        "Send review request email",
        760,
        "{customer_email}",
        "{review_subject}",
        "{review_body}\\n\\n{star_rating_ask}\\n\\nLeave your review: {review_url}",
      ),

      // 5. Review incentive offer
      emailNode(
        "5",
        "Send review incentive offer",
        1000,
        "{customer_email}",
        "A thank-you gift for sharing your thoughts on {product_name}",
        "Hi {customer_first_name},\\n\\nWe would love to hear about your experience with {product_name}. As a thank you for sharing your honest feedback, here is a 10% off code for your next order: REVIEW10\\n\\n{review_prompt}\\n\\nLeave your review: {review_url}\\n\\nThank you for being part of our community!",
      ),

      // 6. Review submission listener (condition)
      n("6", "condition", "Review submitted?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{review_submitted}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 7. Non-review follow-up (delay) — False branch
      n(
        "7",
        "delayAction",
        "Wait 7 days then gentle reminder",
        1480,
        {
          delayDetails: {
            delayType: "fixed",
            fixedDelay: { value: 7, unit: "days" },
          },
        },
        350,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
    ],
  },
};
